import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";

// Context Providers
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";

// Layout Components
import PublicLayout from "./layouts/PublicLayout";
import UserLayout from "./layouts/UserLayout";
import AdminLayout from "./layouts/AdminLayout";

// Public Pages
import HomePage from "./pages/public/HomePage";
import LoginPage from "./pages/public/LoginPage";
import RegisterPage from "./pages/public/RegisterPage";
import AboutPage from "./pages/public/AboutPage";
import ContactPage from "./pages/public/ContactPage";
import PricingPage from "./pages/public/PricingPage";
import CMSPage from "./pages/public/CMSPage";
import QuickShorten from "./pages/public/QuickShorten";

// User Pages
import UserDashboard from "./pages/user/Dashboard";
import LinksPage from "./pages/user/LinksPage";
import CreateLinkPage from "./pages/user/CreateLinkPage";
import LinkStatsPage from "./pages/user/LinkStatsPage";
import EarningsPage from "./pages/user/EarningsPage";
import ReferralsPage from "./pages/user/ReferralsPage";
import ProfilePage from "./pages/user/ProfilePage";
import ApiPage from "./pages/user/ApiPage";

// Admin Pages
import AdminDashboard from "./pages/admin/Dashboard";
import UsersManagement from "./pages/admin/UsersManagement";
import LinksManagement from "./pages/admin/LinksManagement";
import PaymentsManagement from "./pages/admin/PaymentsManagement";
import CountriesManagement from "./pages/admin/CountriesManagement";
import AdsManagement from "./pages/admin/AdsManagement";
import CMSManagement from "./pages/admin/CMSManagement";
import SettingsPage from "./pages/admin/SettingsPage";

// Route Guards
import ProtectedRoute from "./components/common/ProtectedRoute";
import AdminRoute from "./components/common/AdminRoute";

// Link Redirect Component
import LinkRedirect from "./components/LinkRedirect";

// Error Pages
import NotFound from "./pages/errors/NotFound";
import Unauthorized from "./pages/errors/Unauthorized";

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <div className="App min-h-screen bg-gray-50">
            <Routes>
              {/* Public Routes */}
              <Route element={<PublicLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/login" element={<LoginPage />} />
                {/*<Route path="/register" element={<RegisterPage />} />*/}
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/page/:slug" element={<CMSPage />} />
                <Route path="/qs/:apiKey" element={<QuickShorten />} />
              </Route>

              {/* User Dashboard Routes */}
              <Route
                path="/dashboard/*"
                element={
                  <ProtectedRoute>
                    <UserLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<UserDashboard />} />
                <Route path="links" element={<LinksPage />} />
                <Route path="links/create" element={<CreateLinkPage />} />
                <Route path="links/:id/stats" element={<LinkStatsPage />} />
                <Route path="earnings" element={<EarningsPage />} />
                <Route path="referrals" element={<ReferralsPage />} />
                <Route path="api" element={<ApiPage />} />
                <Route path="profile" element={<ProfilePage />} />
              </Route>

              {/* Admin Routes */}
              <Route
                path="/admin/*"
                element={
                  <AdminRoute>
                    <AdminLayout />
                  </AdminRoute>
                }
              >
                <Route index element={<AdminDashboard />} />
                <Route path="users" element={<UsersManagement />} />
                <Route path="links" element={<LinksManagement />} />
                <Route path="payments" element={<PaymentsManagement />} />
                <Route path="countries" element={<CountriesManagement />} />
                <Route path="ads" element={<AdsManagement />} />
                <Route path="cms" element={<CMSManagement />} />
                <Route path="settings" element={<SettingsPage />} />
              </Route>

              {/* Error Routes */}
              <Route path="/unauthorized" element={<Unauthorized />} />
              <Route path="/404" element={<NotFound />} />

              {/* Link Redirect Route - Must be before fallback */}
              <Route path="/:shortCode" element={<LinkRedirect />} />

              {/* Fallback Routes */}
              <Route path="*" element={<Navigate to="/404" replace />} />
            </Routes>

            {/* Global Toast Notifications */}
            <Toaster
              position="top-right"
              reverseOrder={false}
              gutter={8}
              containerClassName=""
              containerStyle={{}}
              toastOptions={{
                // Define default options
                className: "",
                duration: 5000,
                style: {
                  background: "#fff",
                  color: "#374151",
                  borderRadius: "8px",
                  boxShadow:
                    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                },
                // Default options for specific types
                success: {
                  duration: 4000,
                  style: {
                    background: "#10b981",
                    color: "white",
                  },
                  iconTheme: {
                    primary: "white",
                    secondary: "#10b981",
                  },
                },
                error: {
                  duration: 6000,
                  style: {
                    background: "#ef4444",
                    color: "white",
                  },
                  iconTheme: {
                    primary: "white",
                    secondary: "#ef4444",
                  },
                },
                loading: {
                  duration: Infinity,
                },
              }}
            />
          </div>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
